import { useEffect, useState } from "react";
import { ArrowRight, ChevronDown } from "lucide-react";
import { PopupModal } from "react-calendly";
import { useLanguage } from "../i18n/LanguageContext";
import useParallax from "../hooks/useParallax";

const HERO_BG = "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?fit=crop&w=1920&q=80";
const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL;

export default function Hero() {
  const { t } = useLanguage();
  const [loaded, setLoaded] = useState(false);
  const [calendlyOpen, setCalendlyOpen] = useState(false);
  const parallaxY = useParallax(0.35);
  const h = t.hero;

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const d = (ms) => ({ animationDelay: `${ms}ms` });

  return (
    <>
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 will-change-transform" style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center 40%", transform: `translateY(${parallaxY}px)`, top: "-10%", bottom: "-10%" }} />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0F1A2E]/99 via-[#0F1A2E]/96 to-[#0F1A2E]/88" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A2E] via-[#0F1A2E]/70 to-[#0F1A2E]/50" />
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-[#D4AF37] to-transparent opacity-80" />

      {/* Floating gold particles */}
      <div className="hero-particles" aria-hidden="true">
        {[8, 22, 38, 55, 70, 85, 15, 48, 63, 92].map((left, i) => (
          <div key={i} className="hero-particle" style={{
            left: `${left}%`,
            width: `${1.5 + (i % 3)}px`,
            height: `${1.5 + (i % 3)}px`,
            opacity: 0.15 + (i % 4) * 0.08,
            animationDuration: `${8 + i * 2.5}s`,
            animationDelay: `${i * 1.2}s`,
          }} />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-24 w-full">
        {loaded && (
          <>
          <div className="hero-reveal flex items-center gap-3 mb-8" style={d(0)}>
            <div className="h-px w-12 bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs uppercase tracking-widest font-medium">{h.est}</span>
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl text-white leading-[1.05] tracking-tight mb-4 max-w-4xl"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <span className="hero-reveal inline" style={d(200)}>{h.h1[0]}</span>
            <span className="hero-reveal inline gold-gradient-text" style={d(400)}>{h.h1[1]}</span>
            <br />
            <span className="hero-reveal inline gold-gradient-text" style={d(600)}>{h.h1[2]}</span>
            <span className="hero-reveal inline" style={d(800)}>{h.h1[3]}</span>
            <br />
            <span className="hero-reveal inline" style={d(1000)}>{h.h1[4]}</span>
          </h1>

          <p className="hero-reveal text-[#D4AF37]/80 text-sm uppercase tracking-widest mb-5 font-medium" style={d(1200)}>{h.tagline}</p>
          <p className="hero-reveal text-slate-300 text-lg md:text-xl max-w-2xl leading-relaxed mb-10" style={d(1400)}>{h.subtitle}</p>

          <div className="hero-reveal flex flex-col sm:flex-row gap-4" style={d(1600)}>
            <button data-testid="hero-explore-btn" onClick={() => scrollTo("services")}
              className="btn-gold bg-[#D4AF37] text-[#0B1221] px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-[#F59E0B] flex items-center justify-center gap-3 group">
              {h.ctaExplore}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button data-testid="hero-contact-btn" onClick={() => setCalendlyOpen(true)}
              className="border border-[#D4AF37] text-[#D4AF37] px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-[#D4AF37] hover:text-[#0B1221] transition-all duration-300 hover:shadow-[0_0_24px_rgba(212,175,55,0.2)]">
              {h.ctaBook}
            </button>
          </div>

          <div className="hero-reveal flex flex-wrap items-center gap-6 mt-14 pt-10 border-t border-slate-800/60" style={d(1900)}>
            {h.trustClients.map((s) => (
              <span key={s} className="text-slate-300 text-xs uppercase tracking-widest hover:text-slate-300 transition-colors duration-200">{s}</span>
            ))}
            <span className="text-slate-700 text-xs">{h.moreSectors}</span>
          </div>
          </>
        )}
      </div>

      <button onClick={() => scrollTo("stats")}
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-slate-600 hover:text-[#D4AF37] transition-all duration-300 ${loaded ? "hero-reveal" : "opacity-0"}`}
        style={loaded ? d(2200) : undefined}>
        <span className="text-[10px] uppercase tracking-widest">{h.scroll}</span>
        <ChevronDown size={16} className="animate-bounce" />
      </button>
    </section>

    <PopupModal url={CALENDLY_URL} open={calendlyOpen} onModalClose={() => setCalendlyOpen(false)} rootElement={document.getElementById("root")} />
    </>
  );
}
